import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Trash2, Archive } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface ShoppingItem {
  id: string;
  item: string;
  quantity?: number;
  unit?: string | null;
  type: string;
  profile_id: string;
}

const fetchShoppingList = async () => {
  const { data, error } = await supabase
    .from('shopping_list')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
};

const deleteShoppingItem = async (id: string) => {
  const { error } = await supabase
    .from('shopping_list')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
};

const moveToStock = async (item: ShoppingItem) => {
  // Start a transaction by deleting from shopping list and adding to stock list
  const { error: deleteError } = await supabase
    .from('shopping_list')
    .delete()
    .eq('id', item.id);

  if (deleteError) throw deleteError;

  const { error: insertError } = await supabase
    .from('stock_lists')
    .insert([{
      profile_id: item.profile_id,
      item: item.item,
      quantity: item.quantity,
      unit: item.unit,
      type: item.type,
      expiry_date: null
    }]);

  if (insertError) throw insertError;
};

interface ListItemProps {
  item: ShoppingItem;
  onDelete: (id: string) => Promise<void>;
  onMoveToStock: (item: ShoppingItem) => Promise<void>;
}

const ListItem = ({ item, onDelete, onMoveToStock }: ListItemProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMoving, setIsMoving] = useState(false);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await onDelete(item.id);
    } catch (error) {
      console.error('Failed to delete item:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMoveToStock = async () => {
    try {
      setIsMoving(true);
      await onMoveToStock(item);
    } catch (error) {
      console.error('Failed to move item to stock:', error);
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <div className="flex items-center justify-between rounded-lg border p-3 text-sm">
      <div>
        <h4 className="font-medium">{item.item}</h4>
      </div>
      <div className="flex items-center gap-4">
        {(item.quantity !== undefined || item.unit) && (
          <p>
            {item.quantity !== undefined ? item.quantity : ''} {item.unit || ''}
          </p>
        )}
        <Button 
          variant="ghost" 
          size="icon"
          className="h-8 w-8 text-primary hover:text-primary/90"
          onClick={handleMoveToStock}
          disabled={isMoving || isDeleting}
        >
          <Archive className="h-4 w-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon"
          className="h-8 w-8 text-destructive hover:text-destructive/90"
          onClick={handleDelete}
          disabled={isDeleting || isMoving}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export const ShoppingList = ({ 
  open, 
  onOpenChange 
}: { 
  open: boolean; 
  onOpenChange: (open: boolean) => void 
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
    } else {
      const timer = setTimeout(() => {
        setMounted(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const queryClient = useQueryClient();

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['shopping-items'],
    queryFn: fetchShoppingList,
    enabled: open,
    gcTime: 0,
    staleTime: 0
  });

  const handleDelete = async (id: string) => {
    await deleteShoppingItem(id);
    queryClient.invalidateQueries({ queryKey: ['shopping-items'] });
  };

  const handleMoveToStock = async (item: ShoppingItem) => {
    await moveToStock(item);
    queryClient.invalidateQueries({ queryKey: ['shopping-items'] });
    // Optionally invalidate stock list query if it exists
    queryClient.invalidateQueries({ queryKey: ['stock-items'] });
  };

  if (!mounted) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        className={`w-full ${isMobile ? 'h-[100dvh] p-0' : 'max-w-md'}`}
      >
        <SheetHeader className="p-6 pb-2">
          <SheetTitle>Shopping List</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="text-center py-8 px-4">
            <p className="text-gray-500">
              You have nothing in your Shopping list, ask Kitty to fill it up for you!
            </p>
          </div>
        ) : (

          <div className="px-6 mt-4">
        {items.length > 0 && (
            <Alert className="mb-4">
              <Archive className="h-4 w-4" />
              <AlertDescription>
                Use the archive button to move purchased items to your stock list
              </AlertDescription>
            </Alert>
          )}
          {isLoading ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <ScrollArea className="h-[calc(100vh-16rem)] w-full rounded-md border p-4">
              <div className="space-y-4">
                {items.map((item) => (
                  <ListItem 
                    key={item.id} 
                    item={item} 
                    onDelete={handleDelete}
                    onMoveToStock={handleMoveToStock}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
        )}
        
      </SheetContent>
    </Sheet>
  );
};